import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Topbar from './Topbar';
import Sidebar from './Sidebar';
import SearchModal from './SearchModal';
import SettingsModal from './SettingsModal';
import NoteModal from '../notes/NoteModal';
import NoteDetailModal from '../notes/NoteDetailModal';

export default function AppLayout({ children }) {
  // Remember sidebar expansion state on desktop, but NEVER auto-expand on mobile devices
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      return false;
    }
    try {
      const saved = localStorage.getItem('memora:sidebar-expanded');
      return saved !== null ? saved === 'true' : false;
    } catch {
      return false;
    }
  });

  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newNoteOpen, setNewNoteOpen] = useState(false);
  const [inspectNote, setInspectNote] = useState(null);
  const [editNote, setEditNote] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const handleToggleSidebar = () => {
    setSidebarOpen(prev => {
      const next = !prev;
      // Only persist expansion state for desktop/laptop screens
      if (typeof window !== 'undefined' && window.innerWidth >= 992) {
        try {
          localStorage.setItem('memora:sidebar-expanded', String(next));
        } catch {}
      }
      return next;
    });
  };

  const handleCloseSidebar = () => {
    if (window.innerWidth < 992) {
      setSidebarOpen(false);
    }
  };

  // Close sidebar drawer automatically when resizing down to mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 992) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Watch for ?new=1 in URL parameter
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      setNewNoteOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // Global keyboard shortcuts: 'n' or 'c' to create note (outside inputs)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isInput = activeTag === 'input' || activeTag === 'textarea' || document.activeElement?.isContentEditable;
      if (!isInput && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (e.key === 'c' || e.key === 'n') {
          e.preventDefault();
          setNewNoteOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNoteSaved = (savedNote) => {
    window.dispatchEvent(new CustomEvent('memora:note-saved', { detail: savedNote }));
    window.dispatchEvent(new CustomEvent('memora:category-saved', { detail: savedNote }));
    window.dispatchEvent(new CustomEvent('memora:folder-saved', { detail: savedNote }));
  };

  // Check if user is on All Categories vs a Specific Category page
  const categoryMatch = location.pathname.match(/^\/(categories|folders)\/([^/?#]+)/);
  const querySelected = new URLSearchParams(location.search).get('selected');
  const specificCategoryId = categoryMatch ? categoryMatch[2] : (querySelected || null);
  const isAllCategoriesPage = (location.pathname === '/categories' || location.pathname === '/folders') && !querySelected;

  return (
    <div className={`app-shell full-width-layout ${sidebarOpen ? 'sidebar-expanded' : ''}`}>
      {/* Top Navbar */}
      <Topbar
        onToggleSidebar={handleToggleSidebar}
        onOpenSearch={() => setSearchOpen(true)}
        onNewNote={() => setNewNoteOpen(true)}
      />

      <div className="app-main-layout">
        {/* Expandable Sidebar Drawer */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={handleCloseSidebar}
        />

        {/* Main Page Content */}
        <main className="page-body">
          {children}
        </main>
      </div>

      {/* Centered Search Interface with Background Blur */}
      {searchOpen && (
        <SearchModal
          onClose={() => setSearchOpen(false)}
          onSelectNote={note => setInspectNote(note)}
          onSelectCategory={category => {
            setSearchOpen(false);
            navigate(`/categories/${category.id}`);
          }}
          onSelectFolder={folder => {
            setSearchOpen(false);
            navigate(`/categories/${folder.id}`);
          }}
        />
      )}

      {/* Settings Modal */}
      {settingsOpen && (
        <SettingsModal onClose={() => setSettingsOpen(false)} />
      )}

      {/* Create Modal:
          - If on All Categories page: mode is 'category'
          - If on specific Category page: mode is 'note' with that category prefilled!
          - Anywhere else: mode is 'note'
      */}
      {newNoteOpen && (
        <NoteModal
          initialMode={isAllCategoriesPage ? 'category' : 'note'}
          initialCategoryId={specificCategoryId}
          onClose={() => setNewNoteOpen(false)}
          onSaved={handleNoteSaved}
        />
      )}

      {/* Edit Note Modal */}
      {editNote && (
        <NoteModal
          note={editNote}
          onClose={() => setEditNote(null)}
          onSaved={handleNoteSaved}
        />
      )}

      {/* Read / Inspect Note Modal */}
      {inspectNote && (
        <NoteDetailModal
          note={inspectNote}
          onClose={() => setInspectNote(null)}
          onEdit={noteToEdit => {
            setInspectNote(null);
            setEditNote(noteToEdit);
          }}
          onDelete={() => {
            setInspectNote(null);
            window.dispatchEvent(new CustomEvent('memora:note-saved', { detail: inspectNote }));
          }}
        />
      )}
    </div>
  );
}
